import { useFormik, FormikProvider } from 'formik'
import { DateTime } from 'luxon'
import * as yup from 'yup'
import { faBell } from '@fortawesome/pro-regular-svg-icons'
import { useReselector } from '@/utils/sharedHooks'
import { useAppDispatch } from '@/utils/typeHelpers'
import { getCurrentUser } from '@/selectors/user.selectors'
import { updateUser } from '@/actions/userActions'
import { useAnalyticsTrack } from '@/features/amplitude'
import { markUserActionItemCompleteIfExists } from '@/features/user/dashboard/lib/service'
import { UserActionItemActionItemIdentifiers } from '@/features/user/dashboard/user-action-items/lib/userActionItems.slice'

import {
  getFieldName,
  makeReqPhoneNumberSchema,
  makeReqBoolSchema,
} from '@/base'
import { TypographyStyles } from '@/styles'
import { Button, Dialog, Form, Image, Link, Text } from '@/ui'
import {
  createSmsConsentStatus,
  SmsConsentSource,
  SmsConsentStatusOption,
} from '../../../account-details-card/sms-consent/smsConsentReducer'

export const SmsConsentDialog = ({
  source,
  onClose,
  buttonStyle = 'primary',
}: {
  source: SmsConsentSource
  onClose?: () => void
  buttonStyle?: 'primary' | 'ghost'
}) => {
  const currentUser = useReselector(getCurrentUser)
  const dispatch = useAppDispatch()
  const track = useAnalyticsTrack()

  const handleClose = async () => {
    if (currentUser && !currentUser?.smsLoginModalViewedAt) {
      await updateUser(currentUser?.id, {
        smsLoginModalViewedAt: DateTime.now().toISO(),
      })(dispatch)
    }

    if (onClose) {
      onClose()
    }
  }

  const formik = useFormik({
    initialValues: {
      phoneNumber: '',
      consent: false,
    },
    validationSchema: yup.object({
      phoneNumber: makeReqPhoneNumberSchema(),
      consent: makeReqBoolSchema({ trueOnly: true }),
    }),
    onSubmit: async (values) => {
      if (!values.consent) return

      const res = await dispatch(
        createSmsConsentStatus({
          mobilePhoneNumber: values.phoneNumber,
          status: SmsConsentStatusOption.OPT_IN,
          source,
        })
      )

      if (res) {
        await markUserActionItemCompleteIfExists(
          UserActionItemActionItemIdentifiers.smsOptIn,
          track
        )
        await handleClose()
      }
    },
  })

  return (
    <Dialog>
      <Dialog.Trigger asChild>
        {buttonStyle === 'ghost' ? (
          <Button variant="ghost" size="md" iconLeft={faBell}>
            Get Reminders
          </Button>
        ) : (
          <Button iconLeft={faBell} className="w-full md:w-fit">
            Get Reminders
          </Button>
        )}
      </Dialog.Trigger>

      <Dialog.Content>
        <Dialog.Title>
          <Text variant={TypographyStyles.H2}>
            Get Important Tax and Bookkeeping Reminders via SMS
          </Text>
        </Dialog.Title>

        <Image
          src="https://heard-images.s3.amazonaws.com/assets/phone.svg"
          className="w-1/3 mx-auto my-6"
          alt="phone"
        />
        <Dialog.Description>
          We heard you! It&apos;s here! Now you can get important updates,
          reminders, and notifications regarding your practice&apos;s finances
          via SMS, like quarterly payment reminders.
        </Dialog.Description>
        <br />

        <FormikProvider value={formik}>
          <Form.Input
            name={getFieldName<typeof formik.values>('phoneNumber')}
            required
            label="Mobile phone number"
            placeholder="555-555-5555"
            schema={makeReqPhoneNumberSchema()}
            componentType="phone"
          />

          <Form.Checkbox
            name={getFieldName<typeof formik.values>('consent')}
            label="I agree to receive text messages from Heard"
            description={
              <span className="text-muted">
                You agree to receive recurring text messages from Heard about
                account updates and service reminders at the mobile phone number
                provided. Message frequency varies. Message and data rates may
                apply. Reply STOP to cancel. View our{' '}
                <Link newPage href="https://www.joinheard.com/terms-of-service">
                  Terms of Service
                </Link>{' '}
                and{' '}
                <Link newPage href="https://www.joinheard.com/privacy">
                  Privacy Policy.
                </Link>
              </span>
            }
          />
          <br />
          <Dialog.Footer>
            <Button
              onClick={formik.submitForm}
              disabled={!formik.isValid}
              loading={formik.isSubmitting}
            >
              Get Reminders
            </Button>
          </Dialog.Footer>
        </FormikProvider>
      </Dialog.Content>
    </Dialog>
  )
}
